import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface ComboboxOption<T> {
  value: T;
  label: string | React.ReactNode;
  selectedDisplay?: string | React.ReactNode;
  optionDisplay?: string | React.ReactNode;
}

interface ComboboxProps<T> {
  options: ComboboxOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string | React.ReactNode;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
}

export function Combobox<T>({
  options,
  value,
  onChange,
  id,
  ariaLabel,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No option found.',
  className,
  contentClassName,
  disabled = false,
}: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className={cn('w-[200px] justify-between', className)}
          disabled={disabled}
        >
          {value ? options.find(option => option.value === value)?.selectedDisplay : placeholder}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[200px] p-0', className, contentClassName)}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={String(option.value)}
                  value={String(option.label)}
                  onSelect={currentValue => {
                    const selectedOption = options.find(opt => String(opt.label) === currentValue);
                    onChange(
                      selectedOption?.value === value ? null : (selectedOption?.value ?? null),
                    );
                    setOpen(false);
                  }}
                  className={`${value === option.value ? 'border-primary-200 bg-primary-50 border' : ''}`}
                >
                  {option.optionDisplay ?? option.label}
                  <Check
                    className={cn('ml-auto', value === option.value ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
